import React, { useCallback, useEffect, useState } from 'react';
import { IoArrowDown } from 'react-icons/io5';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { Button, cn, Dropdown, Text } from 'rizzui';
import WidgetCard from '../../../components/card/WidgetCard';
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import useFilters from '../../../hooks/useFilters';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { DashboardService } from '../../../services/dashboard/dashboardService';
import { STATUS_TYPE } from '../DashboardFilters';

interface ContractsByStatusProps {
    className?: string;
    variant?: 'default' | 'spotlight';
}

interface ContractStatusData {
    statusId: string | null;
    statusName: string;
    statusColor: string;
    quantity: number;
    contractsListUrl?: string | null;
}

interface ContractsByStatusMeta {
    activeDepartmentIds: string[];
    showCompleteDashboard: boolean;
    hasConfiguredDashboardStatuses: boolean;
    hasConfiguredDashboardStatusesForSelectedType: boolean;
    emptyStateMessage: string | null;
}

const placeholderContracts = [
    { title: 'Contrato 2026-041', subtitle: 'Pendiente de revisar', amount: '3 contratos' },
    { title: 'Contrato 2026-087', subtitle: 'En validación', amount: '5 contratos' },
    { title: 'Contrato 2026-122', subtitle: 'Seguimiento operativo', amount: '2 contratos' },
];

const ContractsByStatus: React.FC<ContractsByStatusProps> = ({ className, variant = 'default' }) => {

    const { filters } = useFiltersPR();
    const { filters: newFilters, updateFilters } = useFilters({ status_type: 'contract', option: "total-contracts-by-status" });
    const { handleErrors } = useHandleErrors();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<ContractStatusData[]>([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [meta, setMeta] = useState<ContractsByStatusMeta>({
        activeDepartmentIds: [],
        showCompleteDashboard: false,
        hasConfiguredDashboardStatuses: false,
        hasConfiguredDashboardStatusesForSelectedType: false,
        emptyStateMessage: null,
    });

    const fetchOrders = useCallback(async (params: any) => {
        setIsLoading(true);
        try {
            const response = await new DashboardService().getTotalContractsByStatus(params);
            const responseData = response.getResponseData()?.data;
            if (responseData) {
                setData(responseData.items ?? []);
                setMeta(responseData.meta ?? {
                    activeDepartmentIds: [],
                    showCompleteDashboard: false,
                    hasConfiguredDashboardStatuses: false,
                    hasConfiguredDashboardStatusesForSelectedType: false,
                    emptyStateMessage: null,
                });
            } else {
                handleErrors(response)
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, [newFilters]);

    const onPieEnter = (_: unknown, index: number) => {
        setActiveIndex(index);
    };

    const handleStatusCountClick = (item: ContractStatusData) => {
        if (!item.contractsListUrl) return;
        navigate(item.contractsListUrl);
    };

    const calculatePercentage = (part: number, total: number) => ((part / total) * 100).toFixed(2);
    const totalContracts = data?.reduce((total, item: ContractStatusData) => total + item.quantity, 0) || 0;
    const isSpotlight = variant === 'spotlight';

    useEffect(() => {
        if (filters.filter_filters?.from && filters.filter_filters?.to) {
            fetchOrders({ filter_filters: { ...newFilters.filter_filters, ...filters.filter_filters } });
        }
    }, [fetchOrders, newFilters, filters]);

    if (isSpotlight) {
        return (
            <WidgetCard
                className={cn(
                    '@container overflow-hidden border-none bg-white px-6 py-6 shadow-[0_18px_45px_-24px_rgba(15,23,42,0.28)] sm:px-7 lg:px-8',
                    className
                )}
                title="Contratos por estado"
                description={
                    <div className="mt-2 max-w-3xl text-sm leading-6 text-gray-600">
                        Visualiza el volumen de contratos disponible para tu departamento y entra directamente al listado filtrado desde cada estado.
                    </div>
                }
                titleClassName="text-xl sm:text-2xl"
                descriptionClassName="text-sm"
                filters={<Dropdown placement="bottom-end">
                    <Dropdown.Trigger>
                        <Button
                            as="span"
                            variant="outline"
                            className="h-10 rounded-md border-gray-200 bg-white px-3 text-sm"
                        >
                            {newFilters.filter_filters?.status_type
                                ? STATUS_TYPE.find((option) => option.value === newFilters.filter_filters?.status_type)?.label
                                : 'Selecciona un estado'}
                            <IoArrowDown className="ml-2" />
                        </Button>
                    </Dropdown.Trigger>
                    <Dropdown.Menu>
                        <Dropdown.Item onClick={() => { updateFilters({ status_type: 'contract' }) }} className='text-nowrap'>Contrato</Dropdown.Item>
                        <Dropdown.Item onClick={() => { updateFilters({ status_type: 'contract_product' }) }} className='text-nowrap'>Producto del contrato</Dropdown.Item>
                        <Dropdown.Item onClick={() => { updateFilters({ status_type: 'contract_invoicing' }) }} className='text-nowrap'>Facturación del contrato</Dropdown.Item>
                        <Dropdown.Item onClick={() => { updateFilters({ status_type: 'contract_incidence' }) }} className='text-nowrap'>Incidencia del contrato</Dropdown.Item>
                        <Dropdown.Item onClick={() => { updateFilters({ status_type: 'contract_commission' }) }} className='text-nowrap'>Comisión del contrato</Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>}
            >
                <div className="mt-6">
                    {!isLoading && meta.emptyStateMessage && (
                        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
                            <p className="font-semibold">No puedes visualizar estadísticas en este momento.</p>
                            <p className="mt-1">{meta.emptyStateMessage}</p>
                            <p className="mt-2 text-amber-800/90">Cuando se configuren estados visibles para tu departamento, este panel mostrará aquí los contadores disponibles.</p>
                        </div>
                    )}

                    {!isLoading && meta.emptyStateMessage ? (
                        <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br from-slate-50 via-white to-slate-100 p-6">
                            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.12),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.10),transparent_28%)]" />
                            <div className="relative grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
                                <div className="space-y-3">
                                    <div className="h-5 w-40 rounded bg-gray-200/80" />
                                    <div className="h-4 w-72 max-w-full rounded bg-gray-100" />
                                    <div className="grid gap-3 pt-2 sm:grid-cols-2">
                                        {placeholderContracts.map((item) => (
                                            <div key={item.title} className="rounded-xl border border-white/80 bg-white/75 p-4 shadow-sm backdrop-blur-sm">
                                                <div className="h-4 w-24 rounded bg-gray-200" />
                                                <div className="mt-3 h-3 w-32 rounded bg-gray-100" />
                                                <div className="mt-6 flex items-center justify-between">
                                                    <div className="h-3 w-20 rounded bg-gray-100" />
                                                    <div className="h-6 w-16 rounded-full bg-blue-100/80" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="rounded-2xl border border-white/70 bg-white/70 p-5 shadow-sm backdrop-blur-sm">
                                    <div className="h-4 w-28 rounded bg-gray-200" />
                                    <div className="mt-5 flex h-[240px] items-end gap-3">
                                        {[44, 72, 58, 84, 66].map((height, index) => (
                                            <div key={index} className="flex-1 rounded-t-xl bg-gradient-to-t from-blue-200/75 to-cyan-100/80" style={{ height: `${height}%` }} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {!isLoading && !meta.emptyStateMessage && data.length > 0 && (
                        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_420px]">
                            <div className="rounded-2xl border border-gray-200 bg-slate-50/70 p-5">
                                <div className="flex flex-wrap items-end justify-between gap-4 border-b border-gray-200 pb-5">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Total visible</p>
                                        <p className="mt-1 text-4xl font-semibold tracking-tight text-gray-900">{totalContracts}</p>
                                    </div>
                                    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-right">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Estados activos</p>
                                        <p className="mt-1 text-lg font-semibold text-gray-900">{data.length}</p>
                                    </div>
                                </div>

                                <div className="mt-5 grid gap-3">
                                    {data.map((item: ContractStatusData) => {
                                        const percentage = calculatePercentage(item.quantity, totalContracts);

                                        return (
                                            <button
                                                key={item.statusName}
                                                type="button"
                                                onClick={() => handleStatusCountClick(item)}
                                                disabled={!item.contractsListUrl}
                                                className={cn(
                                                    'rounded-2xl border border-gray-200 bg-white p-4 text-left transition-all',
                                                    item.contractsListUrl ? 'hover:border-primary/30 hover:shadow-sm' : 'cursor-default'
                                                )}
                                            >
                                                <div className="flex items-center justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span
                                                                className="h-3 w-3 rounded-full"
                                                                style={{ backgroundColor: item.statusColor }}
                                                            />
                                                            <Text as="span" className="truncate text-sm font-semibold text-gray-900">
                                                                {item.statusName}
                                                            </Text>
                                                        </div>
                                                        <Text as="p" className="mt-2 text-xs text-gray-500">
                                                            Acceso directo al listado filtrado por este estado.
                                                        </Text>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-2xl font-semibold text-gray-900">{item.quantity}</p>
                                                        <p className="text-xs font-medium text-gray-500">{percentage}%</p>
                                                    </div>
                                                </div>
                                                <div className="mt-4 h-2 overflow-hidden rounded-full bg-gray-100">
                                                    <div
                                                        className="h-full rounded-full"
                                                        style={{ width: `${percentage}%`, backgroundColor: item.statusColor || '#2563eb' }}
                                                    />
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-5">
                                <div className="mb-3">
                                    <p className="text-sm font-medium text-gray-500">Distribución visual</p>
                                    <p className="text-xs text-gray-500">Resumen proporcional de los estados visibles.</p>
                                </div>
                                <div className="relative mx-auto mt-2 size-[320px] max-w-full">
                                    <ResponsiveContainer
                                        width="100%"
                                        height="100%"
                                        className="relative z-10"
                                    >
                                        <PieChart>
                                            <Pie
                                                cx="50%"
                                                cy="50%"
                                                dataKey="quantity"
                                                innerRadius="58%"
                                                outerRadius="76%"
                                                fill="#8884d8"
                                                data={data}
                                                onMouseEnter={onPieEnter}
                                                activeIndex={activeIndex}
                                                cornerRadius={4}
                                                paddingAngle={3}
                                            >
                                                {data.map((_, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={_?.statusColor || '#8884d8'}
                                                        stroke={_?.statusColor || '#8884d8'}
                                                    />
                                                ))}
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>

                                    <div className="absolute inset-[88px] flex flex-col items-center justify-center rounded-full bg-white shadow-[0px_18px_40px_-24px_rgba(15,23,42,0.45)]">
                                        <Text className="text-center text-gray-500">Contratos visibles</Text>
                                        <Text className="text-3xl font-semibold text-gray-900">
                                            {totalContracts}
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {!isLoading && !meta.emptyStateMessage && data.length === 0 && (
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-5 py-10 text-sm text-gray-600">
                            No hay contratos para los estados y filtros seleccionados.
                        </div>
                    )}
                </div>
            </WidgetCard>
        );
    }

    return (
        <WidgetCard
            className={cn('@container', className)}
            title="Contratos por estado"
            filters={<Dropdown placement="bottom-end">
                <Dropdown.Trigger>
                    <Button
                        as="span"
                        variant="outline"
                    >
                        {newFilters.filter_filters?.status_type
                            ? STATUS_TYPE.find((option) => option.value === newFilters.filter_filters?.status_type)?.label
                            : 'Selecciona un estado'}
                        <IoArrowDown className="ml-2" />
                    </Button>
                </Dropdown.Trigger>
                <Dropdown.Menu>
                    <Dropdown.Item onClick={() => { updateFilters({ status_type: 'contract' }) }} className='text-nowrap'>Contrato</Dropdown.Item>
                    <Dropdown.Item onClick={() => { updateFilters({ status_type: 'contract_product' }) }} className='text-nowrap'>Producto del contrato</Dropdown.Item>
                    <Dropdown.Item onClick={() => { updateFilters({ status_type: 'contract_invoicing' }) }} className='text-nowrap'>Facturación del contrato</Dropdown.Item>
                    <Dropdown.Item onClick={() => { updateFilters({ status_type: 'contract_incidence' }) }} className='text-nowrap'>Incidencia del contrato</Dropdown.Item>
                    <Dropdown.Item onClick={() => { updateFilters({ status_type: 'contract_commission' }) }} className='text-nowrap'>Comisión del contrato</Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>}
        >
            {!isLoading && !meta.showCompleteDashboard && meta.emptyStateMessage && (
                <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <p className="font-medium">No hay datos disponibles para este bloque.</p>
                    <p className="mt-1">{meta.emptyStateMessage}</p>
                    <p className="mt-2 text-amber-800/90">Solicita la configuración de estados del dashboard a administración si necesitas visibilidad en este apartado.</p>
                </div>
            )}

            {!isLoading && data.length === 0 && !meta.emptyStateMessage && (
                <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-600">
                    No hay contratos para los estados y filtros seleccionados.
                </div>
            )}

            {!isLoading && data.length > 0 && (
                <>
                    <div className="relative mx-auto size-[290px] @sm:size-[340px]">
                        <ResponsiveContainer
                            width="100%"
                            height="100%"
                            className="relative z-10"
                        >
                            <PieChart>
                                <Pie
                                    cx="50%"
                                    cy="50%"
                                    dataKey="quantity"
                                    innerRadius="55%"
                                    outerRadius="70%"
                                    fill="#8884d8"
                                    data={data}
                                    onMouseEnter={onPieEnter}
                                    activeIndex={activeIndex}
                                    cornerRadius={2}
                                    paddingAngle={3}
                                    label
                                >
                                    {data.map((_, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={_?.statusColor || '#8884d8'}
                                            stroke={_?.statusColor || '#8884d8'}
                                        />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>

                        <div className="absolute inset-24 flex flex-col items-center justify-center rounded-full bg-white shadow-[0px_4px_20px_0px_#00000029] @sm:inset-28 dark:bg-gray-200">
                            <Text className="text-center text-gray-500">Contratos</Text>
                            <Text className="text-xl font-semibold dark:text-white">
                                {data?.reduce((total, item: ContractStatusData) => total + item.quantity, 0) || 0}
                            </Text>
                        </div>
                    </div>

                    <div className='flex justify-center flex-wrap gap-3'>
                        {data.map((item: ContractStatusData) => (
                            <div key={item.statusName}>
                                <div className='flex items-center gap-1'>
                                    <span
                                        className="me-2 h-2.5 w-3.5 flex-shrink-0"
                                        style={{ backgroundColor: item.statusColor }}
                                    />
                                    <Text as="span" className="whitespace-nowrap">
                                        {item.statusName}
                                    </Text>
                                </div>
                                <Text as="p" className="ms-[12px] flex justify-center">
                                    {calculatePercentage(item.quantity, data?.reduce((total, item: any) => total + item.quantity, 0) || 0)}% (
                                    <button
                                        type="button"
                                        onClick={() => handleStatusCountClick(item)}
                                        disabled={!item.contractsListUrl}
                                        aria-label={`Ver listado de contratos en estado ${item.statusName}`}
                                        className={cn('font-semibold', item.contractsListUrl ? 'cursor-pointer text-primary hover:underline' : 'cursor-default text-gray-600')}
                                    >
                                        {item.quantity}
                                    </button>
                                    )
                                </Text>
                                <Text as="p" className="ms-[26px] flex justify-center">
                                </Text>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </WidgetCard>
    );
};

export default ContractsByStatus;
